import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useLayoutEffect, type ReactNode } from "react";
import { getMultiDragTransform } from "./util/multi-drag-offset";

interface SortableLineProps {
  uuid: string;
  isInDraggedBlock?: boolean;
  /** Row heights this row shifts by so a scattered selection flies as a solid block. */
  collapseShift?: number;
  onDragOffsetChange?: (x: number, y: number) => void;
  children: (dragHandle: ReactNode, isDragging: boolean) => ReactNode;
}

export const SortableLine = ({ uuid, isInDraggedBlock = false, collapseShift = 0, onDragOffsetChange, children }: SortableLineProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: uuid,
    animateLayoutChanges: () => false,
  });

  // dnd-kit only moves the row you grabbed, so that row shares its offset for the rest of the block
  // to copy.
  //  - Read from the row itself, not from how far the pointer moved: the two drift apart when the
  //    page auto-scrolls mid-drag, and the block would split.
  //  - Sent through a CSS variable rather than React state, so tracking the pointer re-renders nothing.
  //  - Run in a layout effect, so the whole block moves in the same frame as the grabbed row.
  useLayoutEffect(() => {
    if (!isDragging || !isInDraggedBlock) return;
    onDragOffsetChange?.(transform?.x ?? 0, transform?.y ?? 0);
  }, [isDragging, isInDraggedBlock, transform?.x, transform?.y, onDragOffsetChange]);

  // The rows following the grabbed one travel with it instead of taking part in the sorting shuffle.
  const followsGrabbedRow = isInDraggedBlock && !isDragging;
  const style: React.CSSProperties = {
    transform: followsGrabbedRow ? getMultiDragTransform(collapseShift) : CSS.Transform.toString(transform),
    transition: followsGrabbedRow ? undefined : transition,
    willChange: followsGrabbedRow ? "transform" : undefined,
    opacity: isDragging || isInDraggedBlock ? 0.5 : undefined,
    // Keep the moving block painted above the rows it passes over.
    position: isInDraggedBlock ? "relative" : undefined,
    zIndex: isInDraggedBlock ? 1 : undefined,
  };

  const dragHandle = (
    <button
      type="button"
      aria-label="Drag to reorder"
      className="text-muted-foreground flex h-6 w-6 cursor-grab touch-none items-center justify-center rounded opacity-0 group-hover:opacity-100 focus:opacity-100 active:cursor-grabbing"
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {children(dragHandle, isDragging)}
    </div>
  );
};
